import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MsalService } from "@azure/msal-angular";
import { Msal2Provider, Providers, TemplateHelper } from "@microsoft/mgt";
import { SilentRequest } from '@azure/msal-browser';
import { ExternalSystemLinkModel } from '../models/navigation.model';


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private readonly msalService: MsalService) { }

  isAuthenticated = new Subject<boolean>();
  userId: string;
  userEmail: string;
  userFullName: string;
  userTableId: number;
  private externalLinks: ExternalSystemLinkModel[];
  private tokenRefreshInterval: any;

  setUserTableId(id: number, links: ExternalSystemLinkModel[]): void {
    this.userTableId = id;
    this.externalLinks = links;
  }

  getExternalSystemLinks(): ExternalSystemLinkModel[] {
    return this.externalLinks || [];
  }

  getAuthData(): any {
    this.configureMgt();

    setTimeout(() => {
      Providers.globalProvider.graph.api('/me').get((err, res) => {
        if (err) {
          console.log(err);
          localStorage.clear();
          sessionStorage.clear();
          window.location.reload();
        } else {
          this.userId = res.id;
          this.userEmail = res.mail;
          this.userFullName = res.displayName;
          this.isAuthenticated.next(true);
        }
      });
    });
  }

  logOut(): void {
    this.isAuthenticated.next(false);
    this.stopTokenRefresh();
    this.msalService.logout();
  }

  startTokenRefresh(): void {
    this.stopTokenRefresh();
    this.tokenRefreshInterval = setInterval(() => {
      void this.refreshAccessToken();
    }, 20 * 60 * 1000);
  }

  stopTokenRefresh(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    try {
      const account = this.msalService.instance.getActiveAccount()
        || this.msalService.instance.getAllAccounts()[0];

      if (!account) {
        console.error("No signed-in account for token refresh");
        return false;
      }
      const apiConfigs = environment.graphToolkit?.apiConfigs || [];
      const scopes = apiConfigs.length > 0
        ? [`${apiConfigs[0].audienceId}/.default`]
        : ['openid', 'profile', 'email'];

      const request: SilentRequest = {
        scopes,
        account,
        forceRefresh: true  // Force refresh from the server
      };

      await this.msalService.instance.acquireTokenSilent(request);
      return true;
    } catch (error) {
      console.error('Failed to refresh access token:', error);

      if (error?.errorCode === 'interaction_required' || 
          error?.errorCode === 'consent_required' ||
          error?.errorCode === 'login_required') {
        console.warn('User interaction required for token refresh');
        this.logOut();
      }
      
      return false;
    }
  }

  private configureMgt(): void {
    Providers.globalProvider = new Msal2Provider({
      publicClientApplication: this.msalService.instance as any,
      options: {
        auth: {
          clientId: environment.graphToolkit.clientId,
          authority: environment.graphToolkit.authority,
        }
      }
    });

    TemplateHelper.setBindingSyntax('[[', ']]');
  }

}
